import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";

import {
  CodeText,
  EmptyState,
  FilterCard,
  JsonBlock,
  PageHeader,
  PageState,
  Select,
  StatusPill,
  TableEmptyRow,
  TableShell,
} from "@/components/common";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNullableDateTimeWithSeconds } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import {
  buildRunImageRegenerationInspectorPath,
  buildRunSearchParams,
  buildRunSelectionSearchParams,
  formatRunDuration,
  formatRunEventTypeLabel,
  formatRunJobTypeLabel,
} from "./runs-state";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type Run = RouterOutputs["admin"]["runs"]["list"][number];
type RunEvent = RouterOutputs["admin"]["runs"]["events"][number];

type RunStatus = Run["status"];
type RunJobType = Run["jobType"];

const RUN_STATUS_CONFIG: Record<RunStatus, { label: string; className: string }> = {
  claimed: {
    label: "Claimed",
    className: "border-slate-200 bg-slate-50 text-slate-700",
  },
  processing: {
    label: "Processing",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  queued_retry: {
    label: "Queued Retry",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
  done: {
    label: "Done",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  failed: {
    label: "Failed",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  requeued_deadlock: {
    label: "Requeued Deadlock",
    className: "border-violet-200 bg-violet-50 text-violet-700",
  },
};

const RUN_JOB_TYPES: RunJobType[] = [
  "origin",
  "discovered",
  "fact",
  "visual_identity",
  "strategy_generation",
  "visual_universe_generation",
  "visual_universe_image_generation",
  "campaign_generation",
  "social_post_generation",
  "social_post_copy_generation",
  "social_post_image_regeneration",
  "social_publish",
  "instagram_media_sync",
];
const RUN_STATUSES = Object.keys(RUN_STATUS_CONFIG) as RunStatus[];

const formatDate = formatNullableDateTimeWithSeconds;

const toIso = (value: string): string | undefined => {
  if (!value.trim()) {
    return undefined;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toISOString();
};

const StatusBadge = ({ status }: { status: RunStatus }) => {
  const config = RUN_STATUS_CONFIG[status];
  return <StatusPill label={config.label} className={config.className} />;
};

const EventCard = ({ event }: { event: RunEvent }) => {
  return (
    <div className="space-y-2 rounded-lg border bg-background p-3 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Badge variant="outline" className="bg-muted/40 font-medium">
          {formatRunEventTypeLabel(event.eventType)}
        </Badge>
        <span className="text-xs text-muted-foreground">{formatDate(event.at)}</span>
      </div>
      {event.message && <p className="text-sm leading-6">{event.message}</p>}
      {event.dataJson && Object.keys(event.dataJson).length > 0 && (
        <JsonBlock value={event.dataJson} />
      )}
    </div>
  );
};

export function RunsPage() {
  const LIMIT = 50;
  const [searchParams, setSearchParams] = useSearchParams();

  const [offset, setOffset] = useState(0);
  const selectedRunId = searchParams.get("runId");

  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [jobType, setJobType] = useState<"" | RunJobType>("");
  const [status, setStatus] = useState<"" | RunStatus>("");
  const [rateLimitFilter, setRateLimitFilter] = useState<"" | "rate_limited">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const queryInput = useMemo(
    () => ({
      limit: LIMIT,
      offset,
      search: search.trim() ? search.trim() : undefined,
      jobType: jobType || undefined,
      status: status || undefined,
      rateLimited: rateLimitFilter === "rate_limited" ? true : undefined,
      from: toIso(from),
      to: toIso(to),
    }),
    [offset, search, jobType, status, rateLimitFilter, from, to],
  );

  const { data: runs, isPending, error } = trpc.admin.runs.list.useQuery(queryInput);
  const { data: summary } = trpc.admin.runs.summary.useQuery({
    search: queryInput.search,
    from: queryInput.from,
    to: queryInput.to,
  });

  const selectedRun = runs?.find((run) => run.id === selectedRunId) ?? null;

  const handleSelectRun = (runId: string) => {
    setSearchParams(buildRunSelectionSearchParams(searchParams, runId), { replace: false });
  };

  const handleClearRun = () => {
    setSearchParams(buildRunSelectionSearchParams(searchParams, null), { replace: false });
  };

  const {
    data: events,
    isPending: eventsPending,
    error: eventsError,
  } = trpc.admin.runs.events.useQuery(
    { runId: selectedRunId ?? "" },
    {
      enabled: selectedRunId !== null,
    },
  );

  if (isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-36" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !runs) {
    return <PageState title="Failed to load runs" tone="danger" />;
  }

  const hasNextPage = runs.length === LIMIT;
  const statusSummaryTotal = summary?.byStatus.reduce((total, item) => total + item.count, 0) ?? 0;
  const jobTypeSummaryTotal =
    summary?.byJobType.reduce((total, item) => total + item.count, 0) ?? 0;
  const statusSummaryDescription = summary
    ? `${statusSummaryTotal} runs in scope`
    : "Loading summary";
  const jobTypeSummaryDescription = summary
    ? `${jobTypeSummaryTotal} runs in scope`
    : "Loading summary";

  return (
    <div className="space-y-4">
      <PageHeader title="Worker Runs" meta={`Showing ${runs.length} runs`} />

      <FilterCard title="Filters" columnsClassName="sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <Input
          placeholder="Search brand, run ID, job ID, error"
          value={search}
          onChange={(event) => {
            const nextSearch = event.target.value;
            setSearch(nextSearch);
            setSearchParams(buildRunSearchParams(searchParams, nextSearch), { replace: true });
            setOffset(0);
          }}
        />

        <Select
          value={jobType}
          onChange={(event) => {
            setJobType((event.target.value as RunJobType) || "");
            setOffset(0);
          }}
        >
          <option value="">All job types</option>
          {RUN_JOB_TYPES.map((item) => (
            <option key={item} value={item}>
              {formatRunJobTypeLabel(item)}
            </option>
          ))}
        </Select>

        <Select
          value={status}
          onChange={(event) => {
            setStatus((event.target.value as RunStatus) || "");
            setOffset(0);
          }}
        >
          <option value="">All statuses</option>
          {RUN_STATUSES.map((item) => (
            <option key={item} value={item}>
              {RUN_STATUS_CONFIG[item].label}
            </option>
          ))}
        </Select>

        <Select
          value={rateLimitFilter}
          onChange={(event) => {
            setRateLimitFilter((event.target.value as "rate_limited") || "");
            setOffset(0);
          }}
        >
          <option value="">All rate states</option>
          <option value="rate_limited">Rate limited only</option>
        </Select>

        <Input
          type="datetime-local"
          value={from}
          onChange={(event) => {
            setFrom(event.target.value);
            setOffset(0);
          }}
        />

        <Input
          type="datetime-local"
          value={to}
          onChange={(event) => {
            setTo(event.target.value);
            setOffset(0);
          }}
        />
      </FilterCard>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.25fr)_minmax(220px,0.55fr)]">
        <Card className="gap-4">
          <CardHeader>
            <CardTitle className="text-sm">By Status</CardTitle>
            <CardDescription>{statusSummaryDescription}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {summary?.byStatus.map((item) => (
              <Badge key={item.status} variant="outline" className="bg-background">
                {RUN_STATUS_CONFIG[item.status].label}: {item.count}
              </Badge>
            ))}
          </CardContent>
        </Card>

        <Card className="gap-4">
          <CardHeader>
            <CardTitle className="text-sm">By Job Type</CardTitle>
            <CardDescription>{jobTypeSummaryDescription}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {summary?.byJobType.map((item) => (
              <Badge
                key={item.jobType}
                variant="outline"
                className="max-w-full whitespace-normal bg-background"
              >
                {formatRunJobTypeLabel(item.jobType)}: {item.count}
              </Badge>
            ))}
          </CardContent>
        </Card>

        <Card className="gap-4">
          <CardHeader>
            <CardTitle className="text-sm">Rate Limits</CardTitle>
            <CardDescription>AI SDK throttling events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2">
              <span className="text-3xl font-semibold tabular-nums">
                {summary ? summary.rateLimitedCount : "—"}
              </span>
              <span className="pb-1 text-sm text-muted-foreground">rate-limited runs</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(320px,380px)]">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="text-sm">Runs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TableShell className="overflow-hidden">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[28%] whitespace-normal">Run</TableHead>
                    <TableHead className="w-[24%] whitespace-normal">Job</TableHead>
                    <TableHead className="w-[16%] whitespace-normal">State</TableHead>
                    <TableHead className="w-[16%] whitespace-normal">Timing</TableHead>
                    <TableHead className="w-[16%] whitespace-normal text-right">
                      Diagnostics
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.length > 0 ? (
                    runs.map((run) => {
                      const imageRegenerationPath = buildRunImageRegenerationInspectorPath(run);
                      return (
                        <TableRow
                          key={run.id}
                          className={
                            run.id === selectedRunId ? "bg-muted/45 align-top" : "align-top"
                          }
                        >
                          <TableCell className="whitespace-normal">
                            <div className="flex min-w-0 flex-col gap-1">
                              <span className="text-xs text-muted-foreground">
                                {formatDate(run.claimedAt)}
                              </span>
                              <span className="truncate text-sm font-medium">
                                {run.sourceDomain ?? "Unknown brand"}
                              </span>
                              <CodeText className="break-all text-[11px]">{run.brandId}</CodeText>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-normal">
                            <div className="flex min-w-0 flex-col gap-1">
                              <span className="text-sm font-medium">
                                {formatRunJobTypeLabel(run.jobType)}
                              </span>
                              <CodeText tone="default">Attempt #{run.attemptNumber}</CodeText>
                              <CodeText className="break-all text-[11px]">
                                {run.workerJobId}
                              </CodeText>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-normal">
                            <div className="flex flex-wrap gap-1.5">
                              <StatusBadge status={run.status} />
                              {run.hasRateLimit && (
                                <Badge
                                  className="border-amber-200 bg-amber-50 text-amber-700"
                                  variant="outline"
                                >
                                  Rate limited
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-normal">
                            <div className="flex flex-col gap-1">
                              <CodeText tone="default" numeric>
                                {formatRunDuration(run.durationMs)}
                              </CodeText>
                              <span className="text-xs text-muted-foreground">
                                Lease {formatDate(run.leaseExpiresAt)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-normal text-right">
                            <div className="flex min-w-0 flex-col items-end gap-2">
                              {run.errorMessage ? (
                                <p
                                  className="max-h-10 overflow-hidden break-words text-right text-xs text-muted-foreground"
                                  title={run.errorMessage}
                                >
                                  {run.errorMessage}
                                </p>
                              ) : (
                                <span className="text-xs text-muted-foreground">No error</span>
                              )}
                              <div className="flex flex-wrap justify-end gap-2">
                                {imageRegenerationPath ? (
                                  <Button asChild size="sm" variant="outline">
                                    <Link to={imageRegenerationPath}>Image</Link>
                                  </Button>
                                ) : null}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSelectRun(run.id)}
                                >
                                  Timeline
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableEmptyRow colSpan={5} className="p-4">
                        <EmptyState description="No runs found." />
                      </TableEmptyRow>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableShell>

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Offset {offset}</span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((value) => Math.max(0, value - LIMIT))}
                  disabled={offset === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((value) => value + LIMIT)}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 2xl:sticky 2xl:top-20 2xl:self-start">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <CardTitle className="text-sm">Timeline</CardTitle>
                <CardDescription className="break-all">
                  {selectedRun
                    ? `${selectedRun.sourceDomain ?? "Unknown brand"} - ${selectedRun.id}`
                    : "Select a run to inspect worker events."}
                </CardDescription>
              </div>
              {selectedRunId ? (
                <Button size="sm" variant="outline" onClick={handleClearRun}>
                  Clear
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedRunId ? (
              <div className="rounded-lg border border-dashed bg-muted/25 p-4 text-sm text-muted-foreground">
                Select a run to inspect its events without losing table context.
              </div>
            ) : eventsPending ? (
              <div className="space-y-2">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : eventsError ? (
              <div className="text-sm text-destructive">Failed to load events.</div>
            ) : events && events.length > 0 ? (
              <div className="max-h-[calc(100vh-16rem)] space-y-2 overflow-y-auto pr-1">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No timeline events"
                description="No worker events have been recorded for this run yet."
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
